import { useState } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Button, Chip, Divider, Paper, TextField, Typography } from '@mui/material';
import type { GridColDef, GridPaginationModel, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { MLDataGrid } from 'ml-react-component-library';

import type { RelatedClientRecord, RelatedClientRow } from '@/apps/engagement-letter-database/data/types';
import { PermissionGate } from '@/core/auth/permissions';

type MemberClient = RelatedClientRecord['Clients'][number];

interface SearchResultsProps {
  rows: RelatedClientRow[];
  onRowClick: (params: GridRowParams<RelatedClientRow>) => void;
  onAddDocument: (clients: MemberClient[]) => void;
  onAddComment: (clients: MemberClient[]) => void;
}

const columns: GridColDef<RelatedClientRow>[] = [
  { field: 'ClientName1', headerName: 'Client Name', flex: 1 },
  { field: 'ClientNumber', headerName: 'Client Number', flex: 1 },
  { field: 'responsibleAttorney', headerName: 'Responsible Attorney', flex: 1 },
  { field: 'practice', headerName: 'Practice', flex: 1 },
  {
    field: 'openDate',
    headerName: 'Open Date',
    flex: 1,
    type: 'date',
    renderCell: (params) => params.value?.toLocaleDateString() ?? '',
  },
  {
    field: 'closeDate',
    headerName: 'Close Date',
    flex: 1,
    type: 'date',
    renderCell: (params) => params.value?.toLocaleDateString() ?? '',
  },
  {
    field: 'isExecuted',
    headerName: 'Executed',
    flex: 1,
    type: 'boolean',
    renderCell: (params) =>
      params.value ? (
        <Chip label="Yes" size="small" color="default" />
      ) : (
        <Chip label="No" size="small" color="warning" />
      ),
  },
];

const SearchResults = ({ rows, onRowClick, onAddDocument, onAddComment }: SearchResultsProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredRows = normalizedSearchTerm
    ? rows.filter(
        (row) =>
          row.ClientName1?.toLowerCase().includes(normalizedSearchTerm) ||
          row.ClientNumber.toLowerCase().includes(normalizedSearchTerm),
      )
    : rows;
  const selectedClients = filteredRows.filter((row) => {
    const isSelected =
      rowSelectionModel.type === 'include'
        ? rowSelectionModel.ids.has(row.ClientNumber)
        : !rowSelectionModel.ids.has(row.ClientNumber);
    return isSelected && row.cmap_clm_engagement_record?.id;
  });
  const hasSelectedRows = selectedClients.length > 0;

  const handleAdd = () => {
    if (hasSelectedRows) onAddDocument(selectedClients);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom>
          Clients
        </Typography>
        <Divider />
        <Box
          sx={{
            paddingTop: 2,
            paddingBottom: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <TextField
            sx={{ width: 325, maxWidth: '100%' }}
            label="Search Clients"
            placeholder="Search by client name or number..."
            size="small"
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                    <SearchIcon />
                  </Box>
                ),
              },
            }}
          />
          <PermissionGate resource="EngagementRecords" action="create">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small" disabled={!hasSelectedRows} onClick={handleAdd}>
                Add Document
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasSelectedRows}
                onClick={() => onAddComment(selectedClients)}
              >
                Add Comment
              </Button>
            </Box>
          </PermissionGate>
        </Box>
      </Box>
      <MLDataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row: RelatedClientRow) => row.ClientNumber}
        onRowClick={onRowClick}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        checkboxSelection
      />
    </Paper>
  );
};

export default SearchResults;
